import React, { useState, useEffect } from 'react';
import { Card, Table, message, Typography, Empty, Tabs, Input, Button, Tag, Space, Modal } from 'antd';
import { SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { teamApi } from '../../services/team/team';
import { teamAuditConfigApi } from '../../services/team/team_audit_config';
import type { TeamEntity } from '../../types/team/team';
import type { TeamAuditConfigEntity, TeamAuditConfigMap } from '../../types/team/team_audit_config';
import TeamAuditConfigUpsertModal from './modals/TeamAuditConfigUpsertModal';
import './TeamAuditConfigs.css';

const { Title, Text } = Typography;

const TeamAuditConfigs: React.FC = () => {
  const [team, setTeam] = useState<TeamEntity | null>(null);
  const [configs, setConfigs] = useState<TeamAuditConfigMap>({});
  const [loading, setLoading] = useState(false);
  const [staffInfo, setStaffInfo] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('A');
  const [searchText, setSearchText] = useState('');

  // Modal states
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<TeamAuditConfigEntity | null>(null);
  const [isEditingDefault, setIsEditingDefault] = useState(false);

  useEffect(() => {
    const staffStr = localStorage.getItem('staff');
    if (staffStr) {
      try {
        setStaffInfo(JSON.parse(staffStr));
      } catch (e) {
        console.error('Failed to parse staff info from localStorage', e);
      }
    }
  }, []);

  useEffect(() => {
    // Load teams where the current user is the leader
    if (staffInfo?.id) {
      teamApi.list({ page: 1, pageSize: 1, filter: { leader_id: staffInfo.id } }).then(res => {
        if (res.code === 200 && res.data && res.data.items?.length > 0) {
          setTeam(res.data.items[0]);
        } else {
          setTeam(null);
        }
      });
    }
  }, [staffInfo]);

  useEffect(() => {
    if (!team) return;
    loadConfigs(team.id);
  }, [team]);

  const loadConfigs = async (teamId: number) => {
    setLoading(true);
    try {
      const res = await teamApi.getConfigs(teamId);
      if (res.code === 200 && res.data) {
        setConfigs(res.data);
      }
    } catch (e: any) {
      message.error(e.message || '加载配置失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingRecord(null);
    setIsEditingDefault(false);
    setIsModalVisible(true);
  };

  const handleEdit = (record: TeamAuditConfigEntity, isDefault: boolean) => {
    setEditingRecord(record);
    setIsEditingDefault(isDefault);
    setIsModalVisible(true);
  };

  const handleSave = async (values: Partial<TeamAuditConfigEntity>) => {
    try {
      if (!team) return;

      const payload: TeamAuditConfigEntity = {
        ...(editingRecord || {}),
        ...values,
        orderType: activeTab,
        teamId: team.id,
      } as TeamAuditConfigEntity;

      if (editingRecord && editingRecord.id) {
        await teamAuditConfigApi.updateWithTeam(team.id, editingRecord.id, payload);
      } else {
        await teamAuditConfigApi.createWithTeam(team.id, payload);
      }
      
      message.success('保存成功');
      setIsModalVisible(false);
      loadConfigs(team.id);
    } catch (e: any) {
      if (e.errorFields) return;
      message.error(e.message || '保存失败');
    }
  };

  const handleDelete = async (record: TeamAuditConfigEntity) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除审核项 "${record.fieldName}" 吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        if (!team) return;
        try {
          if (!record.id) {
             message.error('删除失败: 无效的配置ID');
             return;
          }
          await teamAuditConfigApi.deleteWithTeam(team.id, record.id);
          message.success('删除成功');
          loadConfigs(team.id);
        } catch (e: any) {
          message.error(e.message || '删除失败');
        }
      }
    });
  };

  const renderRequiredTag = (isRequired: boolean) => {
    return isRequired ? (
      <Tag color="error" style={{ borderRadius: 4, padding: '2px 8px', border: '1px solid #ffe2e2' }}>必填项</Tag>
    ) : (
      <Tag style={{ borderRadius: 4, padding: '2px 8px', color: '#6a7282', background: '#f8fafc', border: '1px solid #e5e7eb' }}>选填项</Tag>
    );
  };

  const renderFieldTypeTag = (fieldType: string) => {
    const typeMap: Record<string, { label: string, color: string }> = {
      'text': { label: '文本', color: 'blue' },
      'select': { label: '下拉', color: 'green' },
      'upload': { label: '上传', color: 'purple' },
    };
    const config = typeMap[fieldType || 'text'] || typeMap['text'];
    return <Tag color={config.color} style={{ borderRadius: 4, padding: '2px 8px' }}>{config.label}</Tag>;
  };

  const defaultColumns: ColumnsType<TeamAuditConfigEntity> = [
    { title: '排序', key: 'index', width: 80, render: (_, __, index) => <Text type="secondary" style={{ color: '#d1d5dc' }}>{index + 1}</Text> },
    { title: '审核项名称', dataIndex: 'fieldName', key: 'fieldName', width: 250, render: (text) => <Text strong style={{ color: '#101828' }}>{text}</Text> },
    { title: '字段类型', dataIndex: 'fieldType', key: 'fieldType', width: 120, render: (type) => renderFieldTypeTag(type) },
    { title: '说明要求', dataIndex: 'description', key: 'description', render: (text) => <Text type="secondary" style={{ color: '#6a7282' }}>{text}</Text> },
    { title: '是否必填', dataIndex: 'isRequired', key: 'isRequired', width: 150, render: (isRequired) => renderRequiredTag(isRequired) },
  ];

  const customColumns: ColumnsType<TeamAuditConfigEntity> = [
    { title: '排序', key: 'index', width: 80, render: (_, __, index) => <Text type="secondary" style={{ color: '#d1d5dc' }}>{index + 1}</Text> },
    { title: '审核项名称', dataIndex: 'fieldName', key: 'fieldName', width: 250, render: (text) => <Text strong style={{ color: '#101828' }}>{text}</Text> },
    { title: '字段类型', dataIndex: 'fieldType', key: 'fieldType', width: 120, render: (type) => renderFieldTypeTag(type) },
    { title: '说明要求', dataIndex: 'description', key: 'description', render: (text) => <Text type="secondary" style={{ color: '#6a7282' }}>{text}</Text> },
    { title: '是否必填', dataIndex: 'isRequired', key: 'isRequired', width: 150, render: (isRequired) => renderRequiredTag(isRequired) },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record, false)} style={{ color: '#2b7fff' }}>编辑</Button>
          <Button type="text" icon={<DeleteOutlined />} onClick={() => handleDelete(record)} style={{ color: '#ff4d4f' }}>删除</Button>
        </Space>
      )
    }
  ];

  if (!team) {
    return (
      <div style={{ padding: 24, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Title level={4} style={{ margin: 0, fontSize: 20, color: '#101828' }}>审核项配置</Title>
          </div>
        </div>
        <Card style={{ flex: 1, borderRadius: 14, boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Empty
            description={<span style={{ color: '#667085', fontSize: 16 }}>您暂时未负责团队</span>}
          />
        </Card>
      </div>
    );
  }

  const currentTabConfigs = configs[activeTab] || { default: [], add: [] };
  const defaultConfigs = (currentTabConfigs.default || []).filter(c => c.fieldName.includes(searchText));
  const customConfigs = (currentTabConfigs.add || []).filter(c => c.fieldName.includes(searchText));

  return (
    <div style={{ padding: 24, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Title level={4} style={{ margin: 0, fontSize: 20, color: '#101828' }}>审核项配置</Title>
          <div style={{ 
            background: '#dcfce7', 
            color: '#008236', 
            padding: '4px 10px', 
            borderRadius: 20, 
            fontSize: 12, 
            fontWeight: 500 
          }}>
            权限：团队主管 ({team.name})
          </div>
        </div>
      </div>
      
      <Card 
        style={{ borderRadius: 14, boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.1)', flex: 1, display: 'flex', flexDirection: 'column' }}
        bodyStyle={{ padding: 0, flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab} 
          style={{ padding: '0 24px', borderBottom: '1px solid #e5e7eb' }}
          items={[
            { key: 'A', label: 'A类订单' },
            { key: 'B', label: 'B类订单' },
            { key: 'C', label: 'C类订单' },
          ]}
        />
        
        <div style={{ background: '#f8fafc', padding: 24, flex: 1, overflowY: 'auto' }}>
          {/* Toolbar */}
          <div style={{ 
            display: 'flex', justifyContent: 'space-between', background: '#fff', 
            padding: '16px 20px', borderRadius: 14, marginBottom: 16, 
            border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' 
          }}>
            <Space size="middle">
              <Input 
                placeholder="搜索审核项名称..." 
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />} 
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                style={{ width: 250, borderRadius: 8 }}
              />
            </Space>
            <Button type="primary" icon={<PlusOutlined />} style={{ borderRadius: 8, background: '#2b7fff' }} onClick={handleAdd}>
              新增审核项
            </Button>
          </div>

          {/* Tables Container */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            
            {/* Default Items Section */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '12px 24px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <Text strong style={{ color: '#101828' }}>默认审核项</Text>
                  <Tag style={{ borderRadius: 10, margin: 0, background: '#e5e7eb', color: '#4a5565', border: 'none' }}>{defaultConfigs.length}</Tag>
                </Space>
                <Space style={{ color: '#6a7282', fontSize: 12 }}>
                  <InfoCircleOutlined /> 系统核心配置不可修改名称
                </Space>
              </div>
              <Table 
                rowKey={(record) => record.id ? record.id.toString() : record.fieldName}
                columns={defaultColumns}
                dataSource={defaultConfigs}
                loading={loading}
                pagination={false}
                bordered={false}
                rowClassName={() => 'custom-table-row'}
              />
            </div>

            {/* Custom Items Section */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '12px 24px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <Text strong style={{ color: '#101828' }}>新增审核项</Text>
                  <Tag style={{ borderRadius: 10, margin: 0, background: '#e5e7eb', color: '#4a5565', border: 'none' }}>{customConfigs.length}</Tag>
                </Space>
                <Text style={{ color: '#6a7282', fontSize: 12 }}>支持自定义配置</Text>
              </div>
              <Table 
                rowKey={(record) => record.id ? record.id.toString() : record.fieldName}
                columns={customColumns}
                dataSource={customConfigs}
                loading={loading}
                pagination={false}
                bordered={false}
                rowClassName={() => 'custom-table-row'}
              />
            </div>
            
            {/* Bottom Tip */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: '#f0f5ff', borderRadius: 8, marginTop: 8 }}>
              <InfoCircleOutlined style={{ color: '#2b7fff' }} />
              <Text style={{ color: '#6a7282', fontSize: 13 }}>
                配置提示：设置审核项后，对应类型的订单将在处理流转时强校验该审核项是否已完成核验。
              </Text>
            </div>
          </div>
        </div>
      </Card>

      <TeamAuditConfigUpsertModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSave={handleSave}
        initialValues={editingRecord}
        isDefaultField={isEditingDefault}
      />
    </div>
  );
};

export default TeamAuditConfigs;
